import {
  BarChart3,
  Bell,
  BookOpen,
  Boxes,
  Briefcase,
  Bug,
  CalendarCheck,
  CarFront,
  CheckSquare,
  ClipboardList,
  FileLock2,
  Gavel,
  Inbox,
  GitPullRequestArrow,
  LayoutDashboard,
  History,
  KeyRound,
  LifeBuoy,
  MapPinned,
  MessagesSquare,
  Plug,
  Presentation,
  Scale,
  Settings2,
  ShieldCheck,
  Sparkles,
  Sun,
  Target,
  Truck,
  Umbrella,
  Users,
  CalendarDays,
  ListChecks,
  Clock4,
  Timer,
  Wallet,
  HandCoins,
  ShoppingCart,
  BadgeCheck,
  Palmtree,
  Percent,
  Warehouse,
  PackageCheck,
  type LucideIcon,
} from 'lucide-react';
import type { Permission } from '@nexops/shared';
import type { MessageKey } from '@/i18n/I18nProvider';
import type { PermissionRule } from '@/features/auth/permissions';
import type { AppFeatures } from '@/features/auth/types';

export interface NavLeaf {
  key: string;
  to: string;
  labelKey: MessageKey;
  descriptionKey?: MessageKey;
  permission?: PermissionRule;
  /** Milestone shown on the placeholder page until the module ships. */
  milestone?: string;
  /** Akcent (hex) pozycji podmenu; domyślnie kolor rodzica. */
  color?: string;
  /** Pozycja widoczna tylko przy włączonej fladze funkcji z backendu (`/auth/me` → `features`). */
  feature?: keyof AppFeatures;
}

export interface NavItem extends NavLeaf {
  icon: LucideIcon;
  children?: NavLeaf[];
}

const WORKFLOW: Permission[] = [
  'workflow.start',
  'workflow.approve.prices',
  'workflow.approve.logistics',
  'workflow.approve.orders',
  'workflow.approve.credit',
];

export const ANALYTICS_CHILDREN: NavLeaf[] = [
  { key: 'board', to: '/analytics/board', labelKey: 'nav.analyticsBoard', descriptionKey: 'pages.analyticsBoard', permission: 'bi.board', milestone: 'M2', color: '#f59e0b' },
  { key: 'team', to: '/analytics/team', labelKey: 'nav.analyticsTeam', descriptionKey: 'pages.analyticsTeam', permission: 'bi.team', milestone: 'M2', color: '#3b82f6' },
  { key: 'rep', to: '/analytics/rep', labelKey: 'analytics.nav.mine', permission: 'bi.sales', color: '#14b8a6' },
  { key: 'rep-sales', to: '/analytics/rep-sales', labelKey: 'repSales.nav', permission: 'bi.sales', color: '#0891b2' },
  { key: 'activity', to: '/analytics/activity', labelKey: 'ops.nav.activity', permission: 'bi.sales', color: '#f97316' },
  { key: 'team-reports', to: '/analytics/team-reports', labelKey: 'dayTeam.nav', permission: 'bi.team', color: '#14b8a6' },
  { key: 'funnel', to: '/analytics/funnel', labelKey: 'ops.nav.funnel', permission: 'leads.read', color: '#8b5cf6' },
  { key: 'bonus', to: '/analytics/bonus', labelKey: 'ops.nav.bonus', permission: 'targets.read', color: '#eab308' },
  { key: 'customers', to: '/analytics/customers', labelKey: 'nav.analyticsCustomers', descriptionKey: 'pages.analyticsCustomers', permission: 'bi.sales', color: '#0ea5e9' },
  { key: 'products', to: '/analytics/products', labelKey: 'nav.analyticsProducts', descriptionKey: 'pages.analyticsProducts', permission: 'bi.sales', color: '#ec4899' },
  { key: 'margin', to: '/analytics/margin', labelKey: 'nav.analyticsMargin', descriptionKey: 'pages.analyticsMargin', permission: 'bi.sales', color: '#10b981' },
  { key: 'forecasts', to: '/analytics/forecasts', labelKey: 'nav.analyticsForecasts', descriptionKey: 'pages.analyticsForecasts', permission: ['targets.read', 'bi.board'], color: '#6366f1' },
  { key: 'receivables', to: '/analytics/receivables', labelKey: 'nav.analyticsReceivables', descriptionKey: 'pages.analyticsReceivables', permission: ['bi.receivables', 'receivables.read'], color: '#ef4444' },
  { key: 'profitability', to: '/analytics/profitability', labelKey: 'nav.analyticsProfitability', descriptionKey: 'pages.analyticsProfitability', permission: 'bi.profitability', color: '#22c55e' },
];

/** Flota (pojazdy, leasingi, karty drogowe) — podmenu widoczne tylko z uprawnieniami fleet.*. */
export const FLEET_CHILDREN: NavLeaf[] = [
  { key: 'fleetDashboard', to: '/fleet', labelKey: 'fleet.web.nav.dashboard', permission: 'fleet.analytics', color: '#06b6d4' },
  { key: 'fleetAnalytics', to: '/fleet/analytics', labelKey: 'fleet.web.nav.analytics', permission: 'fleet.analytics', color: '#10b981' },
  { key: 'fleetInsights', to: '/fleet/insights', labelKey: 'fleet.web.ai.nav', permission: 'fleet.insights', color: '#a855f7', feature: 'fleetAi' },
  { key: 'fleetVehicles', to: '/fleet/vehicles', labelKey: 'fleet.nav.vehicles', permission: 'fleet.vehicles.read', color: '#06b6d4' },
  { key: 'fleetLeases', to: '/fleet/leases', labelKey: 'fleet.nav.leases', permission: 'fleet.leases.read', color: '#8b5cf6' },
  { key: 'fleetLogbooks', to: '/fleet/logbooks', labelKey: 'fleet.nav.logbooks', permission: 'fleet.logbook.read', color: '#f97316' },
  { key: 'fleetMyLogbooks', to: '/fleet/my-logbooks', labelKey: 'fleet.nav.myLogbooks', permission: 'fleet.logbook.own', color: '#14b8a6' },
  { key: 'fleetFuel', to: '/fleet/fuel', labelKey: 'fleet.web.nav.fuel', permission: 'fleet.fuel.review', color: '#eab308' },
  { key: 'fleetExpenses', to: '/fleet/expenses', labelKey: 'fleet.web.nav.expenses', permission: 'fleet.expenses.review', color: '#10b981' },
  { key: 'fleetIncidents', to: '/fleet/incidents', labelKey: 'fleet.web.nav.incidents', permission: 'fleet.incidents.manage', color: '#ef4444' },
  { key: 'fleetChecklists', to: '/fleet/checklists', labelKey: 'fleet.web.nav.checklists', permission: 'fleet.logbook.read', color: '#0ea5e9' },
  { key: 'fleetRoutes', to: '/fleet/routes', labelKey: 'fleet.web.nav.routes', permission: 'fleet.logbook.read', color: '#f59e0b' },
  { key: 'fleetService', to: '/fleet/service', labelKey: 'fleet.web.nav.service', permission: 'fleet.vehicles.read', color: '#a855f7' },
  { key: 'fleetTemplates', to: '/fleet/templates', labelKey: 'fleet.web.nav.templates', permission: 'fleet.settings', color: '#6366f1' },
  { key: 'fleetIntegration', to: '/fleet/integration', labelKey: 'fleet.web.nav.integration', permission: 'fleet.integration', color: '#0891b2' },
  { key: 'fleetSettings', to: '/fleet/settings', labelKey: 'fleet.nav.settings', permission: 'fleet.settings', color: '#64748b' },
];

/** Integracje (ERP InsERT GT): dziennik synchronizacji i kolejka do ERP. */
export const INTEGRATIONS_CHILDREN: NavLeaf[] = [
  { key: 'erpSync', to: '/integrations/erp-sync', labelKey: 'erps.nav.log', permission: 'erp.sync.view', color: '#0891b2' },
  { key: 'erpOutbox', to: '/integrations/erp-outbox', labelKey: 'erps.nav.outbox', permission: 'erp.sync.view', color: '#7c3aed' },
];

/** Jakość: pulpit, rejestr reklamacji, analityka (quality.view), zdarzenia i dostawcy (quality.events). */
export const QUALITY_CHILDREN: NavLeaf[] = [
  { key: 'qualityOverview', to: '/quality', labelKey: 'qual.nav.overview', permission: 'quality.view', color: '#8b5cf6' },
  { key: 'qualityComplaints', to: '/quality/complaints', labelKey: 'qual.nav.complaints', permission: 'quality.view', color: '#ef4444' },
  { key: 'qualityAnalytics', to: '/quality/analytics', labelKey: 'qual.nav.analytics', permission: 'quality.view', color: '#10b981' },
  { key: 'qualityEvents', to: '/quality/events', labelKey: 'qual.nav.events', permission: 'quality.events', color: '#f59e0b' },
  { key: 'qualitySuppliers', to: '/quality/suppliers', labelKey: 'qual.nav.suppliers', permission: 'quality.events', color: '#0ea5e9' },
];

/** Sekcje menu: `top` (bez nagłówka, przypięte na górze), potem kolejne grupy. */
export type NavSectionKey = 'top' | 'sales' | 'operations' | 'company' | 'admin';

/**
 * Rozwijana grupa powiązanych pozycji z `NAV_ITEMS` (po kluczach). Grupa z jedną widoczną pozycją rysuje się jak ta
 * pozycja; `labels` podmienia etykietę pozycji wewnątrz grupy (np. „Ubezpieczenia” → „Pulpit ubezpieczeń”).
 */
export interface NavGroupDef {
  group: string;
  labelKey: MessageKey;
  icon: LucideIcon;
  color: string;
  items: string[];
  labels?: Record<string, MessageKey>;
}

export type NavEntryDef = string | NavGroupDef;

/**
 * Układ menu: sekcje → pozycje albo rozwijane grupy. Pozycje z własnym podmenu (Flota, Analityka, Jakość, Integracje)
 * same są grupą. Nowa pozycja menu = dopisz jej klucz do właściwej grupy, nie twórz nowej sekcji.
 */
export const NAV_SECTIONS: Array<{ key: NavSectionKey; entries: NavEntryDef[] }> = [
  {
    key: 'top',
    entries: [
      'ownerDash',
      'mbr',
      'myDay',
      'live',
      { group: 'communication', labelKey: 'layout.groups.communication', icon: MessagesSquare, color: '#d946ef', items: ['chat', 'notifications'] },
    ],
  },
  {
    key: 'sales',
    entries: [
      'customers',
      {
        group: 'salesWork',
        labelKey: 'layout.groups.salesWork',
        icon: Briefcase,
        color: '#8b5cf6',
        items: ['leads', 'visits', 'salesOrders', 'tasks', 'dailyReport', 'surveys'],
      },
      'commissions',
      'analytics',
    ],
  },
  {
    key: 'operations',
    entries: [
      'workflow',
      'logistics',
      { group: 'supply', labelKey: 'layout.groups.supply', icon: Boxes, color: '#16a34a', items: ['purchasing', 'warehouse'] },
      'quality',
      'fleet',
    ],
  },
  {
    key: 'company',
    entries: [
      {
        group: 'team',
        labelKey: 'layout.groups.team',
        icon: CalendarDays,
        color: '#6366f1',
        items: ['planner', 'plannerSetup', 'worktime', 'myWorktime', 'leave', 'worktimeSettings'],
      },
      { group: 'finance', labelKey: 'layout.groups.finance', icon: Wallet, color: '#059669', items: ['cashflow', 'collections', 'costs'] },
      {
        group: 'insurance',
        labelKey: 'layout.groups.insurance',
        icon: Umbrella,
        color: '#2b8fe0',
        items: ['insurance', 'insuranceQueue', 'insuranceClaims', 'insuranceTenders'],
        labels: { insurance: 'layout.groups.insuranceDashboard' },
      },
    ],
  },
  {
    key: 'admin',
    entries: [
      {
        group: 'system',
        labelKey: 'layout.groups.system',
        icon: Settings2,
        color: '#64748b',
        items: ['users', 'roles', 'settings', 'dictionaries', 'audit', 'ai'],
      },
      'integrations',
      { group: 'legal', labelKey: 'layout.groups.legal', icon: Scale, color: '#8b5cf6', items: ['legal', 'gdpr', 'consents'] },
      'feedback',
      'help',
    ],
  },
];

/** Sekcja każdej pozycji z `NAV_ITEMS` (wyprowadzona z `NAV_SECTIONS`). */
export const NAV_SECTION_OF: Record<string, NavSectionKey> = Object.fromEntries(
  NAV_SECTIONS.flatMap((s) => s.entries.flatMap((e) => (typeof e === 'string' ? [e] : e.items)).map((key) => [key, s.key])),
);

/** Pozycja menu gotowa do narysowania; `group` = rozwijana grupa (podmenu pozycji albo grupa z `NAV_SECTIONS`). */
export interface MenuEntry {
  item: NavItem;
  group: boolean;
}

/**
 * Buduje menu z pozycji już przefiltrowanych uprawnieniami (`filterNav`): puste sekcje i grupy znikają,
 * grupa z jedną widoczną pozycją staje się tą pozycją. Pozycja spoza `NAV_SECTIONS` trafia na koniec sekcji „Sprzedaż”.
 */
export function buildMenu(visible: NavItem[]): Array<{ key: NavSectionKey; entries: MenuEntry[] }> {
  const byKey = new Map(visible.map((i) => [i.key, i]));
  const asEntry = (item: NavItem): MenuEntry => ({ item, group: !!item.children?.length });
  return NAV_SECTIONS.map(({ key, entries }) => {
    const out: MenuEntry[] = [];
    for (const e of entries) {
      if (typeof e === 'string') {
        const item = byKey.get(e);
        if (item) out.push(asEntry(item));
        continue;
      }
      const members = e.items.map((k) => byKey.get(k)).filter((i): i is NavItem => !!i);
      if (members.length === 1) out.push(asEntry(members[0]));
      else if (members.length > 1) {
        const children: NavLeaf[] = members.map(({ icon: _icon, children: _children, ...leaf }) => ({
          ...leaf,
          labelKey: e.labels?.[leaf.key] ?? leaf.labelKey,
        }));
        out.push({ item: { key: e.group, to: children[0].to, labelKey: e.labelKey, icon: e.icon, color: e.color, children }, group: true });
      }
    }
    if (key === 'sales') out.push(...visible.filter((i) => !(i.key in NAV_SECTION_OF)).map(asEntry));
    return { key, entries: out };
  }).filter((s) => s.entries.length > 0);
}

/** Main navigation. Items the user cannot access are hidden (backend still enforces access). */
export const NAV_ITEMS: NavItem[] = [
  { key: 'ownerDash', to: '/manager', labelKey: 'od.nav', descriptionKey: 'od.description', icon: LayoutDashboard, permission: 'dashboard.owner' },
  // Raport zarządu (miesięczny) — zarząd i kierownik czytają, działy wprowadzają dane do zamknięcia miesiąca.
  { key: 'mbr', to: '/mbr', labelKey: 'mbr.common.nav', descriptionKey: 'mbr.common.subtitle', icon: Presentation, permission: ['mbr.read', 'mbr.write'] },
  { key: 'myDay', to: '/', labelKey: 'nav.myDay', icon: Sun },
  { key: 'live', to: '/admin/live', labelKey: 'ops.nav.live', icon: MapPinned, permission: 'bi.team' },
  { key: 'customers', to: '/customers', labelKey: 'nav.customers', icon: Users, permission: 'customers.read' },
  { key: 'leads', to: '/leads', labelKey: 'nav.leads', descriptionKey: 'pages.leads', icon: Target, permission: 'leads.read', milestone: 'M2' },
  { key: 'visits', to: '/visits', labelKey: 'nav.visits', descriptionKey: 'pages.visits', icon: CalendarCheck, permission: 'visits.read', milestone: 'M3' },
  // Zamówienia od handlowców (NX-419): kolejka Biura zamówień — wprowadzenie do Subiektu, odrzucenie; licznik „do wprowadzenia”.
  { key: 'salesOrders', to: '/sales/orders', labelKey: 'sord.nav', icon: PackageCheck, permission: 'orders.manage' },
  { key: 'tasks', to: '/tasks', labelKey: 'nav.tasks', descriptionKey: 'pages.tasks', icon: CheckSquare, permission: 'tasks.read', milestone: 'M2' },
  // Raport dzienny handlowca — dla osób rejestrujących wizyty (handlowiec, kierownik); API: customers.read.
  { key: 'dailyReport', to: '/my-daily-report', labelKey: 'dailySheet.nav', icon: ClipboardList, permission: 'visits.write' },
  { key: 'chat', to: '/chat', labelKey: 'chat.nav', icon: MessagesSquare, permission: 'chat.use' },
  { key: 'notifications', to: '/notifications', labelKey: 'notifs.nav', icon: Bell },
  { key: 'workflow', to: '/workflow', labelKey: 'nav.workflow', descriptionKey: 'pages.workflow', icon: GitPullRequestArrow, permission: WORKFLOW, milestone: 'M2' },
  { key: 'logistics', to: '/logistics', labelKey: 'nav.logistics', descriptionKey: 'pages.logistics', icon: Truck, permission: 'logistics.read' },
  {
    key: 'fleet',
    to: '/fleet',
    labelKey: 'fleet.nav.title',
    icon: CarFront,
    permission: FLEET_CHILDREN.flatMap((c) => (typeof c.permission === 'string' ? [c.permission] : c.permission ?? [])),
    children: FLEET_CHILDREN,
  },
  // Planer i RCP (NX-413, jak w ZUR OPS): grafik zespołu, czynności i atrybuty zmian, karty czasu pracy, własna karta, reguły.
  { key: 'planner', to: '/planner', labelKey: 'pln.common.nav.planner', icon: CalendarDays, permission: 'planner.view' },
  { key: 'plannerSetup', to: '/planner/setup', labelKey: 'pln.common.nav.setup', icon: ListChecks, permission: 'planner.manage' },
  { key: 'worktime', to: '/worktime', labelKey: 'pln.common.nav.worktime', icon: Clock4, permission: 'worktime.view' },
  { key: 'myWorktime', to: '/worktime/my', labelKey: 'pln.common.nav.myWorktime', icon: Timer, permission: 'worktime.own' },
  { key: 'worktimeSettings', to: '/worktime/settings', labelKey: 'pln.common.nav.settings', icon: ShieldCheck, permission: 'worktime.settings' },
  // Urlopy (NX-421): salda z InsERT i NEXOPS, kalendarz, wnioski do decyzji — zarząd, administrator, kierownicy (zakres danych).
  { key: 'leave', to: '/leave', labelKey: 'lv.nav', icon: Palmtree, permission: 'leave.view' },
  // Ubezpieczenia (jak w ZUR OPS): pulpit z polisami, szkodowością, importem i ustawieniami + kolejka, szkody, przetargi.
  { key: 'insurance', to: '/insurance', labelKey: 'ins.common.nav.dashboard', icon: Umbrella, permission: 'insurance.view' },
  { key: 'insuranceQueue', to: '/insurance/queue', labelKey: 'ins.common.nav.queue', icon: Inbox, permission: 'claims.qualify' },
  { key: 'insuranceClaims', to: '/insurance/claims', labelKey: 'ins.common.nav.claims', icon: ClipboardList, permission: 'claims.view' },
  { key: 'insuranceTenders', to: '/insurance/tenders', labelKey: 'ins.common.nav.tenders', icon: Gavel, permission: 'tenders.manage' },
  // Finanse → Cash flow (etap 2 rozszerzeń LiluFresh): kasy, bank, zobowiązania, prognoza płynności — dane całej firmy.
  { key: 'cashflow', to: '/finance/cashflow', labelKey: 'cf.nav', icon: Wallet, permission: 'cashflow.view' },
  {
    key: 'analytics',
    to: '/analytics',
    labelKey: 'nav.analytics',
    descriptionKey: 'pages.analytics',
    icon: BarChart3,
    permission: ANALYTICS_CHILDREN.flatMap((c) => (typeof c.permission === 'string' ? [c.permission] : c.permission ?? [])),
    children: ANALYTICS_CHILDREN,
  },
  // Prawo (jak w ZUR OPS)
  { key: 'legal', to: '/legal', labelKey: 'nav.legal', descriptionKey: 'pages.legal', icon: Scale, milestone: 'M3' },
  { key: 'gdpr', to: '/admin/gdpr', labelKey: 'nav.gdpr', descriptionKey: 'pages.gdpr', icon: FileLock2, permission: 'gdpr.manage' },
  { key: 'consents', to: '/admin/consents', labelKey: 'nav.consents', descriptionKey: 'pages.consents', icon: ShieldCheck, permission: 'admin.users', milestone: 'M3' },
  // System (jak w ZUR OPS)
  { key: 'users', to: '/admin/users', labelKey: 'nav.users', descriptionKey: 'pages.users', icon: Users, permission: 'admin.users', milestone: 'M2' },
  { key: 'roles', to: '/admin/roles', labelKey: 'nav.roles', descriptionKey: 'pages.roles', icon: KeyRound, permission: 'admin.users', milestone: 'M2' },
  { key: 'settings', to: '/admin/settings', labelKey: 'nav.settings', descriptionKey: 'pages.settings', icon: Settings2, permission: 'admin.settings' },
  { key: 'dictionaries', to: '/admin/dictionaries', labelKey: 'nav.dictionaries', descriptionKey: 'pages.dictionaries', icon: BookOpen, permission: 'admin.settings' },
  { key: 'audit', to: '/admin/audit', labelKey: 'nav.audit', descriptionKey: 'pages.audit', icon: History, permission: 'admin.audit', milestone: 'M2' },
  {
    key: 'integrations',
    to: '/integrations/erp-sync',
    labelKey: 'erps.nav.group',
    icon: Plug,
    permission: 'erp.sync.view',
    children: INTEGRATIONS_CHILDREN,
  },
  // Jakość (etap 3b): reklamacje z ERP ze statusem obsługi, analityka, zdarzenia jakościowe, dostawcy.
  {
    key: 'quality',
    to: '/quality',
    labelKey: 'qual.nav.group',
    icon: BadgeCheck,
    permission: ['quality.view', 'quality.events'],
    children: QUALITY_CHILDREN,
  },
  { key: 'ai', to: '/admin/ai', labelKey: 'ai.usage.nav', descriptionKey: 'ai.usage.description', icon: Sparkles, permission: 'admin.settings' },
  { key: 'feedback', to: '/feedback', labelKey: 'nav.feedback', descriptionKey: 'pages.feedback', icon: Bug, milestone: 'M3' },
  { key: 'help', to: '/help', labelKey: 'nav.help', descriptionKey: 'pages.help', icon: LifeBuoy, milestone: 'M3' },
  // Koszty z księgi Rewizora (etap 4a): kadry wg działów, flota wg pojazdów, produkcja, mapa kont — zarząd i administrator.
  { key: 'costs', to: '/costs', labelKey: 'costs.nav', descriptionKey: 'costs.subtitle', icon: Wallet, permission: 'costs.view' },
  // Finanse → Windykacja (X1): należności, lista wstydu, Top 40, sprawy w kancelarii; handlowiec — swoi klienci.
  { key: 'collections', to: '/finance/collections', labelKey: 'coll.nav', icon: HandCoins, permission: 'collections.view' },
  // Zakupy (etap 3a): prognoza, do zamówienia, dostawcy — zarząd, administrator, logistyka.
  { key: 'purchasing', to: '/purchasing', labelKey: 'pur.nav', icon: ShoppingCart, permission: 'purchasing.view' },
  // Sprzedaż → Prowizje (dodatek prowizyjny InsERT): handlowiec — własne, kierownik — zespół, zarząd — wszystkie.
  { key: 'commissions', to: '/sales/commissions', labelKey: 'com.nav', icon: Percent, permission: 'commissions.view' },
  // Ankiety klientów (kreator dla zarządu i kierownika): szablony, wyniki, eksport.
  { key: 'surveys', to: '/surveys', labelKey: 'svy.nav', descriptionKey: 'svy.list.subtitle', icon: ClipboardList, permission: 'surveys.manage' },
  // Magazyn (Etap 6): normy i wykonanie kompletacji — kierownik magazynu, zarząd, administrator.
  { key: 'warehouse', to: '/warehouse', labelKey: 'wh.nav', icon: Warehouse, permission: 'warehouse.view' },
];

/** Filters nav items (and their children) by a permission checker. */
export function filterNav(items: NavItem[], can: (rule: PermissionRule) => boolean, features: AppFeatures = { fleetAi: false }): NavItem[] {
  const on = (i: NavLeaf) => !i.feature || features[i.feature];
  return items
    .filter((item) => on(item) && can(item.permission))
    .map((item) => (item.children ? { ...item, children: item.children.filter((c) => on(c) && can(c.permission)) } : item))
    .filter((item) => !item.children || item.children.length > 0);
}
